import { Link } from 'react-router-dom';
import { Brain, HeartPulse, Activity, Stethoscope, Users, FileText } from 'lucide-react';
import { Hero } from '../../components/Hero';

const services = [
  {
    title: 'Traumatic Brain Injuries',
    description: 'Specialized care and rehabilitation for patients who have experienced traumatic brain injuries, focusing on recovery and functional improvement.',
    icon: Brain,
    href: '/services/traumatic-brain-injuries',
  },
  {
    title: 'Dysautonomia',
    description: 'Expert diagnosis and treatment of autonomic nervous system disorders, including POTS, orthostatic hypotension, and other autonomic dysfunctions.',
    icon: HeartPulse,
    href: '/services/dysautonomia',
  },
  {
    title: 'Headaches',
    description: 'Comprehensive treatment for various types of headaches, including migraines, tension headaches, and cluster headaches.',
    icon: Activity,
    href: '/services/headaches',
  },
  {
    title: 'Functional Neurologic Disorder',
    description: 'Specialized care for patients with functional neurologic disorders, addressing symptoms through an interdisciplinary rehabilitation approach.',
    icon: Stethoscope,
    href: '/services/fnd',
  },
  {
    title: 'Neurodevelopmental Disorders',
    description: 'Evaluation and treatment of conditions affecting neurological development, with personalized strategies for both children and adults.',
    icon: Users,
    href: '/services/neurodevelopmental',
  },
  {
    title: 'Movement Disorders',
    description: 'Expert diagnosis and treatment of conditions affecting movement, utilizing advanced therapies to improve motor function and patient independence.',
    icon: Activity,
    href: '/services/movement-disorders',
  },
];

export function Overview() {
  return (
    <div>
      <Hero
        title="Our Services"
        description="Comprehensive neurological care delivered with expertise, compassion, and cutting-edge technology."
        image="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80"
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service) => {
          const Icon = service.icon;
          return (
            <Link
              key={service.title}
              to={service.href}
              className="group bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600 mb-4">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                {service.title}
              </h3>
              <p className="text-gray-600">
                {service.description}
              </p>
              <div className="mt-4 flex items-center text-blue-600 font-medium">
                Learn more
                <FileText className="ml-2 h-4 w-4" />
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-16 bg-blue-50 rounded-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Choose Juncos Neuro Consulting?</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our comprehensive approach to neurological care combines expert diagnosis, 
            innovative treatments, and personalized attention to achieve the best possible outcomes.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-lg p-6">
            <Brain className="h-8 w-8 text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Team</h3>
            <p className="text-gray-600">
              Our specialists bring decades of experience in treating complex neurological conditions.
            </p>
          </div>
          <div className="bg-white rounded-lg p-6">
            <Activity className="h-8 w-8 text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Technology</h3>
            <p className="text-gray-600">
              Access to the latest diagnostic tools and treatment technologies for optimal care.
            </p>
          </div>
          <div className="bg-white rounded-lg p-6">
            <HeartPulse className="h-8 w-8 text-blue-600 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Personalized Care</h3>
            <p className="text-gray-600">
              Individualized treatment plans tailored to your specific needs and goals.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-16 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Take the First Step?</h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          Schedule a consultation with our expert team to discuss your neurological health concerns 
          and discover how we can help you achieve better health.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
        >
          Schedule a Consultation
          <FileText className="ml-2 h-5 w-5" />
        </Link>
      </div>
    </div>
  );
}